import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { useRouter } from '@popety-io/ui'
import { useMap } from '../../contexts/MapContext'
import { useLandsUrl } from '../hooks/useLandsUrl'
import {
  landSearchService,
  type BackendLand,
} from '../services/landSearchService'
import { getAllTags } from '../services/landTagsService'
import type {
  LandsState,
  LandsActions,
  LandSearchFilters,
  LandSearchSort,
} from '../types/landFilters.types'
import type { TagWithUserInfo } from '@popety-io/types'
import { DEFAULT_LAND_FILTERS } from '../utils/defaultFilters'

interface LandsContextType extends LandsState, LandsActions {}

const LandsContext = createContext<LandsContextType | undefined>(undefined)

const LIST_VIEW_FIELDS = [
  // Identifiers
  'land_id',
  'egrid_nb',

  // Location
  'address_full',
  'municipality_name',
  'region_name',
  'address_postal_code_name',

  // Basic property info
  'area',
  'type',

  // Building details
  'building_nb',
  'unit_nb',
  'average_building_year',

  // Zoning/planning
  'main_lupa_name',
  'main_lupa_type',
  'main_lupa_cover_area',
  'cos',
  'cus',
  'lupa_number',

  // Scores for radar chart
  'development_score',
  'under_exploited_score',
  'poi_score',
  'livability_score',
  'existing_score',

  // Activity data
  'listings',
  'transactions',
  'construction_permits',
  'transaction_number',
  'listing_rent_number',
  'listing_sale_number',
  'construction_permit_number',

  // Links
  'rf_uri',
]

type LandsAction =
  | { type: 'SET_LANDS'; payload: BackendLand[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_TOTAL'; payload: number }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_FILTERS'; payload: LandSearchFilters }
  | { type: 'SET_DEBOUNCED_FILTERS'; payload: LandSearchFilters }
  | { type: 'SET_SORT'; payload: LandSearchSort[] }
  | { type: 'SET_SELECTED_LAND_ID'; payload: string | undefined }
  | { type: 'SET_FILTER_MODE'; payload: 'manual' | 'ai' }
  | { type: 'SET_AVAILABLE_TAGS'; payload: TagWithUserInfo[] }
  | { type: 'SET_TAGS_LOADING'; payload: boolean }
  | { type: 'CLEAR_FILTERS' }
  | {
      type: 'SYNC_FROM_URL'
      payload: {
        filters: LandSearchFilters
        sort: LandSearchSort[]
        page: number
        pageSize: number
      }
    }

const initialState: LandsState = {
  lands: [],
  loading: false,
  error: null,
  total: 0,
  currentPage: 1,
  pageSize: 20,
  filters: {},
  debouncedFilters: {},
  sort: [{ field: 'area', order: 'desc' }],
  selectedLandId: undefined,
  filterMode: 'manual',
  availableTags: [],
  tagsLoading: false,
  fields: [
    'id',
    'land_id',
    'address_full',
    'municipality_name',
    'address_number',
    'official_area',
    'area',
    'building_nb',
    'unit_nb',
    'development_score',
    'under_exploited_score',
    'existing_score',
    'livability_score',
    'geo_shape_score',
    'lupa_number',
    'main_lupa_name',
    'main_lupa_type',
    'main_lupa_cover_area',
    'code_number',
    'building_area',
    'restriction_type',
    'official_usage_indice_extracted',
    'average_building_year',
    'unit_nb',
    'highest_building_protected_rank',
    'official_occupation_indice_extracted',
    'construction_permit_last_year',
    'listing_rent_last_year',
    'listing_sale_last_year',
    'transaction_last_year',
  ],
}

function landsReducer(state: LandsState, action: LandsAction): LandsState {
  switch (action.type) {
    case 'SET_LANDS':
      return { ...state, lands: action.payload }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_TOTAL':
      return { ...state, total: action.payload }
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, currentPage: 1 }
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, currentPage: 1 }
    case 'SET_DEBOUNCED_FILTERS':
      return { ...state, debouncedFilters: action.payload, currentPage: 1 }
    case 'SET_SORT':
      return { ...state, sort: action.payload }
    case 'SET_SELECTED_LAND_ID':
      return { ...state, selectedLandId: action.payload }
    case 'SET_FILTER_MODE':
      return { ...state, filterMode: action.payload }
    case 'SET_AVAILABLE_TAGS':
      return { ...state, availableTags: action.payload }
    case 'SET_TAGS_LOADING':
      return { ...state, tagsLoading: action.payload }
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: DEFAULT_LAND_FILTERS,
        debouncedFilters: DEFAULT_LAND_FILTERS,
        currentPage: 1,
      }
    case 'SYNC_FROM_URL': {
      // Only update if values actually changed to prevent loops
      const needsUpdate =
        JSON.stringify(state.filters) !==
          JSON.stringify(action.payload.filters) ||
        JSON.stringify(state.sort) !== JSON.stringify(action.payload.sort) ||
        state.currentPage !== action.payload.page ||
        state.pageSize !== action.payload.pageSize

      if (!needsUpdate) return state

      return {
        ...state,
        filters: action.payload.filters,
        debouncedFilters: action.payload.filters,
        sort: action.payload.sort,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize,
      }
    }
    default:
      return state
  }
}

interface LandsProviderProps {
  children: ReactNode
}

export const LandsProvider: React.FC<LandsProviderProps> = ({ children }) => {
  const { query } = useRouter()
  const { viewMode, propertyPanelOpen } = useMap()
  const { urlState, updateFilters, updateSort, updatePagination } =
    useLandsUrl(propertyPanelOpen)

  // Get current mode from URL
  const currentMode = (query.mode as 'plot' | 'building') || 'plot'

  // Initialize state from URL
  const initialStateFromUrl: LandsState = {
    ...initialState,
    filters: urlState.filters,
    debouncedFilters: urlState.filters,
    sort: urlState.sort.length > 0 ? urlState.sort : initialState.sort,
    currentPage: urlState.page,
    pageSize: urlState.pageSize,
  }

  const [state, dispatch] = useReducer(landsReducer, initialStateFromUrl)
  const isUpdatingFromUrl = useRef(false)
  const lastUrlStateRef = useRef(JSON.stringify(urlState))

  const setLands = (lands: BackendLand[]) => {
    dispatch({ type: 'SET_LANDS', payload: lands })
  }

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading })
  }

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error })
  }

  const setTotal = (total: number) => {
    dispatch({ type: 'SET_TOTAL', payload: total })
  }

  const setCurrentPage = (page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page })
    if (!isUpdatingFromUrl.current) {
      updatePagination(page, state.pageSize)
    }
  }

  const setPageSize = (size: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size })
    if (!isUpdatingFromUrl.current) {
      updatePagination(1, size) // Reset to page 1 when changing page size
    }
  }

  const setFilters = (filters: LandSearchFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters })
    if (!isUpdatingFromUrl.current) {
      updateFilters(filters)
    }
  }

  const setSort = (sort: LandSearchSort[]) => {
    dispatch({ type: 'SET_SORT', payload: sort })
    if (!isUpdatingFromUrl.current) {
      updateSort(sort)
    }
  }

  const setSelectedLandId = (id?: string) => {
    dispatch({ type: 'SET_SELECTED_LAND_ID', payload: id })
  }

  const setFilterMode = (mode: 'manual' | 'ai') => {
    dispatch({ type: 'SET_FILTER_MODE', payload: mode })
  }

  const clearFilters = () => {
    dispatch({ type: 'CLEAR_FILTERS' })
    if (!isUpdatingFromUrl.current) {
      updateFilters({}) // Empty object for URL - defaults applied by reducer
    }
  }

  const updateFilter = useCallback(
    <K extends keyof LandSearchFilters>(
      key: K,
      value: LandSearchFilters[K]
    ) => {
      const newFilters = { ...state.filters, [key]: value }

      setFilters(newFilters)
    },
    [state.filters]
  )

  const updateMultipleFilters = useCallback(
    (updates: Partial<LandSearchFilters>) => {
      const newFilters = { ...state.filters, ...updates }
      setFilters(newFilters)
    },
    [state.filters]
  )

  // Cascading clear function for location and dependent filters
  const clearLocationAndDependentFilters = useCallback(() => {
    const updates: Partial<LandSearchFilters> = {
      // Clear all location-related filters
      location_search: undefined,
      location_type: undefined,
      municipality: undefined,
      municipality_id: undefined, // Clear ID as we're clearing the municipality
      district: undefined,
      region: undefined,
      npa: undefined,
      region_id: undefined, // Clear region_id for restriction filter visibility
      // Clear dependent filters that require municipality
      plan: undefined,
      assignment: undefined,
    }

    updateMultipleFilters(updates)
  }, [updateMultipleFilters])

  // Clear municipality specifically (when removed from chips/tags)
  const clearMunicipalityAndDependents = useCallback(() => {
    const updates: Partial<LandSearchFilters> = {
      municipality: undefined,
      municipality_id: undefined, // Clear ID as we're clearing the municipality
      // Clear dependent filters that require municipality_id
      plan: undefined,
      assignment: undefined,
    }

    // Also clear location_search if it matches municipality
    if (state.filters.location_type === 'municipality') {
      updates.location_search = undefined
      updates.location_type = undefined
    }

    updateMultipleFilters(updates)
  }, [state.filters, updateMultipleFilters])

  // Debounce filter changes for text inputs
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set new timeout to update debounced filters after 500ms
    debounceTimeoutRef.current = setTimeout(() => {
      dispatch({ type: 'SET_DEBOUNCED_FILTERS', payload: state.filters })
    }, 500)

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [state.filters])

  const searchLands = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)

    try {
      const request = {
        filters: state.debouncedFilters,
        limit: state.pageSize,
        offset: (state.currentPage - 1) * state.pageSize,
        sort: state.sort,
        fields: LIST_VIEW_FIELDS,
      }

      const response = await landSearchService.searchWithNewFilters(request)

      setLands(response.data)
      setTotal(response.total)
    } catch (error) {
      console.error('Error searching lands:', error)
      setError(
        error instanceof Error
          ? error.message
          : 'An error occurred while searching lands'
      )
    } finally {
      setLoading(false)
    }
  }, [state.debouncedFilters, state.pageSize, state.currentPage, state.sort])

  const loadLand = async (id: string): Promise<BackendLand | null> => {
    try {
      // For now, find from existing lands data since we don't have a detailed endpoint
      const land = state.lands.find((l) => l.id === id || l.land_id === id)
      return land || null
    } catch (error) {
      console.error('Error loading land:', error)
      setError(
        error instanceof Error
          ? error.message
          : 'An error occurred while loading land'
      )
      return null
    }
  }

  const triggerSearch = useCallback(() => {
    // Force a search regardless of view mode (for manual triggers)
    searchLands()
  }, [searchLands])

  // Sync state filters to URL (when filters change) - only in plot mode
  useEffect(() => {
    if (currentMode !== 'plot') return

    if (!isUpdatingFromUrl.current) {
      updateFilters(state.filters)
    }
  }, [state.filters, updateFilters, currentMode])

  // Trigger search when debouncedFilters change
  useEffect(() => {
    if (viewMode === 'list' && currentMode === 'plot') {
      searchLands()
    }
  }, [state.debouncedFilters, searchLands, viewMode, currentMode])

  // Sync with URL changes (only when URL changes externally)
  useEffect(() => {
    const currentUrlState = JSON.stringify(urlState)

    // Only update if URL actually changed
    if (currentUrlState !== lastUrlStateRef.current) {
      lastUrlStateRef.current = currentUrlState
      isUpdatingFromUrl.current = true

      dispatch({
        type: 'SYNC_FROM_URL',
        payload: {
          filters: { ...state.filters, ...urlState.filters },
          sort: urlState.sort,
          page: urlState.page,
          pageSize: urlState.pageSize,
        },
      })

      // Reset flag after a tick to allow state updates
      setTimeout(() => {
        isUpdatingFromUrl.current = false
      }, 0)
    }
  }, [urlState])

  // Tags Management
  const setAvailableTags = useCallback((tags: TagWithUserInfo[]) => {
    dispatch({ type: 'SET_AVAILABLE_TAGS', payload: tags })
  }, [])

  const setTagsLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_TAGS_LOADING', payload: loading })
  }, [])

  const fetchTags = useCallback(async () => {
    try {
      setTagsLoading(true)
      const tags = await getAllTags()
      // Transform tags to TagWithUserInfo format if needed
      const formattedTags: TagWithUserInfo[] = tags.map((tagRelation: any) => {
        // Handle LandTagRelation structure
        const tag = tagRelation.tag || tagRelation
        return {
          id: tag.id,
          value: tag.value || tag.name,
          color: tag.color || '#4A90E2',
          user: tag.user || null,
        }
      })
      setAvailableTags(formattedTags)
    } catch (error) {
      console.error('Failed to fetch tags:', error)
      setAvailableTags([])
    } finally {
      setTagsLoading(false)
    }
  }, [setAvailableTags, setTagsLoading])

  // Fetch tags when component mounts
  useEffect(() => {
    fetchTags()
  }, [])

  const contextValue: LandsContextType = {
    ...state,
    setLands,
    setLoading,
    setError,
    setTotal,
    setCurrentPage,
    setPageSize,
    setFilters,
    setSort,
    setSelectedLandId,
    setFilterMode,
    clearFilters,
    searchLands,
    loadLand,
    updateFilter,
    updateMultipleFilters,
    triggerSearch,
    clearLocationAndDependentFilters,
    clearMunicipalityAndDependents,
    setAvailableTags,
    setTagsLoading,
    fetchTags,
  }

  return (
    <LandsContext.Provider value={contextValue}>
      {children}
    </LandsContext.Provider>
  )
}

export const useLands = (): LandsContextType => {
  const context = useContext(LandsContext)
  if (context === undefined) {
    throw new Error('useLands must be used within a LandsProvider')
  }
  return context
}
